import React, {useRef} from 'react';
import {useIntl} from 'react-intl';
import {FormGroup, Input, Label} from 'reactstrap';
import ReactSelect from '../../components/react-select';
import {Form} from '@unform/web';
import {Input as UInput} from '../../components/input';
import * as Yup from 'yup';
import ToggleButton from '../../components/toggle-button';

import './geofence.sass';
import I18nMessage from '../../components/I18nMessage';
import {FieldSet} from '../../components/fieldset';
import {TextArea} from '../../components/textarea';
import MapDraw from './map-draw';
import {GroupButtonForm} from '../../components/group-button-form';
import {ReactColor} from '../../components/react-color';
import {kmhToKnots, knotsToKmH} from '../../helpers/conversions';
import useStore from '../../hooks/use-store';

const GeofenceForm = (props) => {
    const {
        cancel,
        data,
        edit,
        newRegister,
        updateRegister,
        calendars,
        loading,
    } = props;

    const intl = useIntl();
    const formRef = useRef(null);
    // An administrator reaches nearly every device, so the API refuses a geofence of theirs marked for
    // all devices; the field is not offered to them.
    const isAdministrator = Boolean(useStore()?.store?.session?.administrator);

    const schema = Yup.object().shape({
        id: Yup.number(),
        name: Yup.string().required(),
        description: Yup.string().notRequired(),
        calendarId: Yup.string().notRequired(),
        allDevices: Yup.string().notRequired(),
        area: Yup.string().required(),
        attributes: Yup.object().shape({
            color: Yup.string().notRequired(),
            speedLimit: Yup.string().notRequired(),
            polylineDistance: Yup.string().notRequired(),
        }),
    });

    const handleSubmit = async (payload) => {
        try {
            await schema.validate(payload, {
                abortEarly: false,
            });

            payload = schema.cast(payload);

            if (payload.attributes) {
                const attributes = Object.assign({}, payload.attributes);
                if (attributes.hasOwnProperty('speedLimit')) {
                    if (attributes.speedLimit.length > 0) {
                        attributes.speedLimit = parseFloat(kmhToKnots(payload.attributes.speedLimit));
                    } else {
                        delete attributes.speedLimit;
                    }
                }
                if (attributes.hasOwnProperty('polylineDistance')) {
                    if (attributes.polylineDistance.length > 0) {
                        attributes.polylineDistance = parseFloat(payload.attributes.polylineDistance);
                    } else {
                        delete attributes.polylineDistance;
                    }
                }
                if (attributes.hasOwnProperty('color')) {
                    if (attributes.color.length === 0) {
                        delete attributes.color;
                    }
                }
                payload.attributes = attributes;
            }
            edit ? updateRegister(payload) : newRegister(payload);
        } catch (err) {
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    const cast = (src) => {
        const data = Object.assign({}, src);
        if (data.attributes) {
            const attributes = Object.assign({}, data.attributes);
            if (attributes.hasOwnProperty('speedLimit')) {
                attributes.speedLimit = knotsToKmH(attributes.speedLimit);
            }

            data.attributes = attributes;
        }
        return data;
    };

    const setMapArea = (area) => {
        formRef.current.setFieldValue('area', area);
    };

    return (
        <Form
            ref={formRef}
            onSubmit={handleSubmit}
            schema={schema}
            initialData={cast(data)}>
                {!!edit && <UInput hidden type="number" name="id"/>}
                <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIdentification"/>}>
                    <div className={'row'}>
                        <div className={'col'}>
                            {!!edit && <UInput hidden type="number" name="id"/>}
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedName"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="name"
                                    id="name"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="groupId"><I18nMessage id="sharedCalendar"/></Label>
                                <ReactSelect
                                    id={'calendarId'}
                                    name="calendarId"
                                    options={calendars}/>
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedDescription"/></Label>
                                <TextArea
                                    type="text"
                                    name="description"
                                    id="description"
                                />
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>

                <FieldSet className="text-left border p-4" title={intl.formatMessage({id: 'mapTitle'})}>
                    <>
                        <MapDraw
                            key={`map__draw`}
                            data={data}
                            setData={(data) => setMapArea(data)}
                        />
                        <UInput hidden type="text" name="area"/>
                    </>
                </FieldSet>

                <FieldSet className="text-left border p-4" title={<I18nMessage id="settingsTitle"/>}>
                    <div className={'row'}>
                        <div className={'col'}>
                            {!isAdministrator && (
                                <FormGroup className="custom-formGroup">
                                    <Label>{intl.formatMessage({id: 'geofenceAllDevices'})}</Label>
                                    <ToggleButton name={'allDevices'} id={'allDevices'}/>
                                </FormGroup>
                            )}
                            <FormGroup className="custom-formGroup">
                                <Label for="name">{intl.formatMessage({id: 'geofenceColor'})}</Label>
                                <ReactColor
                                    name="attributes.color"
                                    id="color"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="groupId">{intl.formatMessage({id: 'geofenceLineDistance'})}</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="attributes.polylineDistance"
                                    id="polylineDistance"
                                />
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name">{intl.formatMessage({id: 'geofenceSpeedLimit'})}</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="attributes.speedLimit"
                                    id="speedLimit"
                                />
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>

                <br/>
                <GroupButtonForm
                    loading={loading}
                    cancel={cancel}
                    submit={() => formRef.current.submitForm()}
                />

            </Form>
    );
};

export default GeofenceForm;
